import { enqueueSnackbar } from 'notistack';
import { create } from 'zustand';

import { authRepository } from 'src/auth/repositories/auth.repository';
import { setSession, clearSession, getStoredTokens } from 'src/auth/utils/utils';
import { useMeStore } from 'src/store/me/me.store';
import { getSiteName } from 'src/utils/host-name';

type States = {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
};

type Actions = {
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  refreshAccessToken: (refreshToken: string) => Promise<string | null>;
  login: (username: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
};

type AuthStore = States & Actions;

const initialState: States = {
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),

  setLoading: (isLoading) => set({ isLoading }),

  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });

      const authData = await authRepository.login({ email, password, host: getSiteName() });

      // Definir sessão e estado de autenticação
      setSession(authData.accessToken, authData.refreshToken);

      // Definir dados do usuário diretamente do webAppUser
      const meStore = useMeStore.getState();
      meStore.setMeFromLogin(authData.user);

      set({
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return authData;
    } catch (error) {
      const errorMessage = error.error ? error.error : 'Erro no login';
      set({
        isLoading: false,
        error: errorMessage,
      });
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
      throw error;
    }
  },

  refreshAccessToken: async (refreshToken: string) => {
    try {
      const accessToken = await authRepository.refreshAccessToken(refreshToken);
      return accessToken;
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar o token', { variant: 'error' });
      set({ error: error instanceof Error ? error.message : 'Erro ao atualizar o token' });
      throw error;
    }
  },

  logout: async () => {
    try {
      const { refreshToken } = getStoredTokens();
      try {
        await authRepository.logout(refreshToken ?? '');
      } catch {
        // Mesmo que o logout falhe, prosseguir para limpar a sessão local
      }
      clearSession();
      set({
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });

      // Limpar dados do usuário quando fizer logout
      const meStore = useMeStore.getState();
      meStore.clearMe();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  },
}));
